'use client'

import Link from 'next/link'
import type { ComponentProps, ReactNode } from 'react'
import styles from './Buttons.module.scss'

type CommonButtonProps = {
  children: ReactNode
  startIcon?: ReactNode
  endIcon?: ReactNode
}

type LinkButtonProps = CommonButtonProps &
  Omit<ComponentProps<typeof Link>, 'className' | 'children'> & {
    href: ComponentProps<typeof Link>['href']
  }

type NativeButtonProps = CommonButtonProps &
  Omit<ComponentProps<'button'>, 'className' | 'children'> & {
    href?: undefined
  }

type ButtonProps = LinkButtonProps | NativeButtonProps

function renderContent({ children, startIcon, endIcon }: CommonButtonProps) {
  return (
    <>
      {startIcon && <span className={styles.startIcon}>{startIcon}</span>}
      {children}
      {endIcon && <span className={styles.endIcon}>{endIcon}</span>}
    </>
  )
}

function createButton(className: string): React.FC<ButtonProps> {
  return function Button(props) {
    if ('href' in props && props.href !== undefined) {
      const { children, startIcon, endIcon, ...linkProps } = props
      return (
        <Link {...linkProps} className={className} data-button>
          {renderContent({ children, startIcon, endIcon })}
        </Link>
      )
    }

    const {
      children,
      startIcon,
      endIcon,
      type = 'button',
      ...buttonProps
    } = props
    return (
      <button {...buttonProps} type={type} className={className} data-button>
        {renderContent({ children, startIcon, endIcon })}
      </button>
    )
  }
}

export const PrimaryButton = createButton(styles.primary ?? '')

export const SecondaryButton = createButton(styles.secondary ?? '')
